import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useConfig } from '../../contexts/config';
import { useChat } from '../../contexts/ChatContext';
import { getBroadcastRecipients } from '../../services/moodle/chat';
import BroadcastComposer from './BroadcastComposer';

// Container: owns recipient fetch, selection state, 40-cap enforcement,
// submit, and error handling. Recipients are fetched directly from the
// service (not through ChatContext.canBroadcast, which only carries the
// boolean gate) — see design.md Data Flow.
const BroadcastComposerContainer = ({ courseId, onBack }) => {
  const { user } = useAuth();
  const { config } = useConfig();
  const { sendBroadcast } = useChat();

  const [students, setStudents] = useState([]);
  const [maxRecipients, setMaxRecipients] = useState(40);
  const [recipientsLoading, setRecipientsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadRecipients = async () => {
      if (!user || !user.token || !courseId) return;
      setRecipientsLoading(true);
      try {
        const data = await getBroadcastRecipients(user.token, config.baseURL, courseId);
        if (cancelled) return;
        setStudents(data.students || []);
        setMaxRecipients(data.maxrecipients || 40);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading broadcast recipients:', err);
        setError('No se pudieron cargar los alumnos. Intenta nuevamente.');
      } finally {
        if (!cancelled) setRecipientsLoading(false);
      }
    };

    loadRecipients();
    return () => {
      cancelled = true;
    };
  }, [user, config, courseId]);

  const handleToggleRecipient = useCallback((id) => {
    setSelectedIds(prev => (
      prev.includes(id) ? prev.filter(existing => existing !== id) : [...prev, id]
    ));
  }, []);

  // "Select all" checks every student, even past the cap — the Send button
  // stays disabled until the tutor manually deselects down to maxRecipients.
  // Never silently truncates the selection.
  const handleSelectAll = useCallback(() => {
    setSelectedIds(students.map(s => s.id));
  }, [students]);

  const handleClearAll = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const handleRequestSend = () => {
    if (selectedIds.length === 0 || selectedIds.length > maxRecipients || !text.trim()) return;
    setConfirming(true);
  };

  const handleCancelConfirm = () => {
    setConfirming(false);
  };

  const handleConfirmSend = async () => {
    setSending(true);
    setError(null);
    try {
      const result = await sendBroadcast(courseId, selectedIds, text.trim());
      setConfirming(false);
      setSuccessMessage(`Mensaje enviado a ${result.recipientcount} alumnos`);
      // On success: brief confirmation, then auto-return to the contacts list.
      setTimeout(() => {
        onBack();
      }, 1200);
    } catch (err) {
      console.error('Error sending broadcast:', err);
      setConfirming(false);
      // Keep composer open, preserve selection + text so the tutor doesn't
      // have to redo it — only surface the error.
      setError(err.message || 'No se pudo enviar el anuncio. Intenta nuevamente.');
    } finally {
      setSending(false);
    }
  };

  if (successMessage) {
    return (
      <div className="w-full flex flex-col h-full items-center justify-center gap-3 p-6 text-center">
        <div className="text-2xl">✅</div>
        <p className="text-xl font-semibold">{successMessage}</p>
      </div>
    );
  }

  return (
    <BroadcastComposer
      onBack={onBack}
      students={students}
      recipientsLoading={recipientsLoading}
      selectedIds={selectedIds}
      onToggleRecipient={handleToggleRecipient}
      onSelectAll={handleSelectAll}
      onClearAll={handleClearAll}
      maxRecipients={maxRecipients}
      text={text}
      onTextChange={setText}
      sending={sending}
      error={error}
      confirming={confirming}
      onRequestSend={handleRequestSend}
      onConfirmSend={handleConfirmSend}
      onCancelConfirm={handleCancelConfirm}
    />
  );
};

export default BroadcastComposerContainer;
